import React, { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Autocomplete,
  Box,
  Card,
  Checkbox,
  Grid,
  InputAdornment,
  Tooltip,
  Typography,
} from "@mui/material";
import MainLayout from "../../../layouts/MainLayout";
import PageHeader from "../../../components/PageHeader";
import useNotification from "../../../hooks/useNotification";
import useLoader from "../../../hooks/useLoader";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import Button from "../../../components/Button";
import { equipmentFields } from "../../../constants/forms/equipmentFields";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useOrgan } from "../../../hooks/useOrgan";
import { organizationPrimaryColors } from "../../../layouts/PermanentSidebarMUI";
import StyledTextField from "../../../components/shared/StyledTextField";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";
import { useAuth } from "../../../hooks/useAuth";
import echo from "../../../utils/echo";

const CreateEquipmentPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { get: baseServiceGet, post: baseServicePost } =
    useBaseService(navigate);

  const { control, handleSubmit, watch, setValue, reset, setError } = useForm({
    defaultValues: {
      product_item: null,
      equipments: [],
    },
  });

  const { replace } = useFieldArray({
    control,
    name: "equipments",
  });

  // --- Buscar equipamentos ---
  const { data: equipmentData = [], isLoading } = useQueryWithLoader({
    queryKey: ["equipmentOptions"],
    queryFn: async () => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        is_completed: false,
        status_id: 1,
      });

      const params = { filters, perPage: 99999 };
      const res = await baseServiceGet(entities.equipments.get, params);
      return res.result.data; // apenas retorna o data original
    },
  });

  // --- Unificar e transformar os dados ---
  const equipmentOptions = useMemo(() => {
    if (!equipmentData?.length) return [];

    const uniqueData = Object.values(
      equipmentData.reduce((acc, item) => {
        const key = `${item.creation_transaction?.id}_${item.name}`;
        acc[key] = item;
        return acc;
      }, {}),
    );

    return uniqueData.map((equipment) => ({
      value: equipment.id,
      label: `T#${equipment.creation_transaction?.id} - ${equipment.name}`,
      raw: equipment,
    }));
  }, [equipmentData]);

  const selectedEquipment = watch("product_item");

  // --- Atualizar campos ao escolher equipamento ---
  useEffect(() => {
    if (selectedEquipment?.raw) {
      const eq = selectedEquipment.raw;
      setValue("number", String(eq.number).trim() || "");
      setValue("serial_number", String(eq.serial_number).trim() || "");
    }
  }, [selectedEquipment, setValue]);

  // Quando seleciona um equipamento
  useEffect(() => {
    if (!selectedEquipment) return;

    const related = equipmentData.filter(
      (eq) =>
        eq.transaction_item_id === selectedEquipment.raw.transaction_item_id,
    );

    // garante que o array tem o tamanho correto
    replace(
      related.map((eq) => ({
        id: eq.id,
        number: "",
        serial_number: "",
        serial_required: eq.serial_required ?? true,
      })),
    );

    // limpa explicitamente os campos
    related.forEach((_, index) => {
      setValue(`equipments.${index}.number`, "");
      setValue(`equipments.${index}.serial_number`, "");
    });
  }, [selectedEquipment, equipmentData, replace, setValue]);

  // --- Mutação para criar ---
  const createEquipment = useMutation({
    mutationFn: async (payload) => {
      showLoader();

      const response = await baseServicePost(
        entities.equipments.createMany,
        payload,
        "equipment",
      );
      return response;
    },
    onSuccess: (response) => {
      hideLoader();
      if (response?.status === 201) {
        queryClient.invalidateQueries(["equipments"]);
        queryClient.invalidateQueries(["lastEquipmentNumber"]);
        reset();
        showNotification("Equipamento cadastrado com sucesso!");
        navigate("/aluguel/equipamentos/");
      }
    },
    onError: (err, variables) => {
      hideLoader();
      console.error("onError", err);

      if (err?.data && typeof err.data === "object") {
        Object.entries(err.data).forEach(([path, messages]) => {
          // path exemplo: "equipment.0.number"
          const match = path.match(/equipment\.(\d+)\.(.+)/);

          if (match) {
            const sentIndex = parseInt(match[1]); // índice no array filtrado (0, 1, 2...)
            const fieldName = match[2]; // "number" ou "serial_number"

            // Descobre qual era o índice original na tela usando o payload enviado
            const originalIndex =
              variables.equipment[sentIndex]?.original_index;

            if (originalIndex !== undefined) {
              const formPath = `equipments.${originalIndex}.${fieldName}`;
              const message = Array.isArray(messages) ? messages[0] : messages;

              setError(formPath, {
                type: "server",
                message,
              });
              return;
            }
          }

          // Fallback caso a lógica acima não encontre o índice
          const fallbackPath = path.replace(/^equipment/, "equipments");
          setError(fallbackPath, {
            type: "server",
            message: Array.isArray(messages) ? messages[0] : messages,
          });
        });
      } else {
        showNotification("error", err.message || "Erro desconhecido.");
      }
    },
  });

  const onSubmit = (data) => {
    try {
      // 1. Identifica itens que possuem QUALQUER informação preenchida
      const equipmentsWithData = data.equipments
        .map((eq, index) => ({
          ...eq,
          originalIndex: index,
        }))
        .filter(
          (eq) =>
            (eq.number && eq.number.trim() !== "") ||
            (eq.serial_number && eq.serial_number.trim() !== ""),
        );

      if (equipmentsWithData.length === 0) {
        showNotification(
          "warning",
          "Preencha os dados de pelo menos um equipamento.",
        );
        return;
      }

      let validationError = false;

      // 2. Validação cruzada
      equipmentsWithData.forEach((eq) => {
        const hasNumber = eq.number && eq.number.trim() !== "";
        const hasSerial = eq.serial_number && eq.serial_number.trim() !== "";

        // Se preencheu serial mas não o tombo -> Erro no Tombo
        if (hasSerial && !hasNumber) {
          setError(`equipments.${eq.originalIndex}.number`, {
            type: "manual",
            message: "Não esqueça de preencher o tombo.",
          });
          validationError = true;
        }

        // Se marcou como obrigatório mas não preencheu serial -> Erro no Serial
        // (Mesmo que tenha tombo)
        if (eq.serial_required && !hasSerial) {
          setError(`equipments.${eq.originalIndex}.serial_number`, {
            type: "manual",
            message: "O serial é obrigatório para este item.",
          });
          validationError = true;
        }
      });

      if (validationError) return;

      const payload = {
        equipment: data.equipments
          .map((eq, index) => ({ ...eq, originalIndex: index }))
          .filter((eq) => eq.number && eq.number.trim() !== "") // Filtra apenas quem tem tombo
          .map((eq) => ({
            id: eq.id,
            transaction_item_id: selectedEquipment.raw.transaction_item_id,
            product_item_id: selectedEquipment.raw.product_item_id,
            name: selectedEquipment.raw.name,
            number: String(eq.number).trim(),
            serial_required: eq.serial_required,
            serial_number: eq.serial_number ? String(eq.serial_number) : null,
            is_completed: 1,
            address_id: selectedEquipment.raw.address_id,
            location_id: selectedEquipment.raw.location_id,
            current_organization_id:
              selectedEquipment.raw.current_organization_id,
            status_id: selectedEquipment.raw.status_id,
            original_index: eq.originalIndex,
            completed_by: user.id,
          })),
      };

      createEquipment.mutate(payload);
    } catch (error) {
      console.error("Erro ao concluir cadastro de equipamento:", {
        status: error.status,
        message: error.message,
      });
    }
  };

  // --- Buscar último tombo cadastrado ---
  const { data: lastNumberData } = useQuery({
    queryKey: ["lastEquipmentNumber"],
    queryFn: async () => {
      const res = await baseServiceGet(entities.equipments.lastNumber);
      return res.result.last_number;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
  });

  // Para busca o valor dentro de equipmentInfo
  function getNestedValue(obj, path) {
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
  }

  const { selectedOrgan } = useOrgan();
  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM;

  useEffect(() => {
    // Escuta o canal público 'equipments'
    const channel = echo
      .channel("equipments")
      .listen(".number.updated", (e) => {
        queryClient.setQueryData(["lastEquipmentNumber"], e.lastNumber);

        showNotification(
          "info",
          `O último tombo foi atualizado para: ${e.lastNumber}`,
        );
      });

    return () => {
      channel.stopListening(".number.updated");
    };
  }, [queryClient, showNotification]);

  const handleToggleAllSerials = (checked) => {
    const currentEquipments = watch("equipments");
    currentEquipments.forEach((_, index) => {
      setValue(`equipments.${index}.serial_required`, checked);
    });
  };

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Concluir Cadastro de Equipamento"
        showBackButton
        backUrl="/equipamentos/"
      />

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          pr: 1,
          py: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          maxWidth: "100%", // usar toda a largura disponível
        }}
      >
        {/* === SEÇÃO SELECIONAR === */}
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              backgroundColor: primaryColor,
              py: 1,
              px: 2,
              color: "#ffffff",
              borderTopLeftRadius: "5px",
              borderTopRightRadius: "5px",
            }}
          >
            Selecionar Equipamento
          </Typography>
          <Card
            variant="outlined"
            sx={{
              p: 2,
              borderTopLeftRadius: "0px",
              borderTopRightRadius: "0px",
            }}
          >
            <Controller
              name="product_item"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={equipmentOptions}
                  getOptionLabel={(option) => option?.label ?? ""}
                  noOptionsText="Nenhum equipamento pendente para ser cadastrado. Caso necessário, entre em contato com o almoxarifado."
                  isOptionEqualToValue={(opt, val) => opt.value === val.value}
                  onChange={(_, newValue) => field.onChange(newValue)}
                  loading={isLoading}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      label="Equipamento"
                      size="small"
                      fullWidth
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      placeholder="Selecione o Equipamento"
                    />
                  )}
                />
              )}
            />
          </Card>
        </Box>

        {selectedEquipment && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* === DETALHES === */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
                {equipmentFields.map((section) => {
                  const equipmentInfo = equipmentData.find(
                    (eq) =>
                      eq.transaction_item_id ===
                      selectedEquipment.raw.transaction_item_id,
                  );

                  if (!equipmentInfo) return null;

                  return (
                    <Box key={section.section} sx={{ mb: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: "text.secondary",
                          fontWeight: 600,
                          mb: 2,
                          textTransform: "uppercase",
                        }}
                      >
                        {section.section}
                      </Typography>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, 1fr)",
                          gap: 2,
                        }}
                      >
                        {section.fields.map((field) => {
                          let value = getNestedValue(equipmentInfo, field.id);
                          // se o valor for data (por exemplo: 2025-11-05), converte-lo para DD/MM/YYYY
                          if (field.type === "date" && value) {
                            value = new Date(value).toLocaleDateString("pt-BR");
                          }
                          return (
                            <StyledTextField
                              key={field.id}
                              label={field.label}
                              value={value ?? "-"}
                              size="small"
                              fullWidth
                              disabled
                              slotProps={{
                                input: {
                                  startAdornment: field.icon ? (
                                    <InputAdornment position="start">
                                      <FontAwesomeIcon
                                        icon={field.icon}
                                        size="sm"
                                      />
                                    </InputAdornment>
                                  ) : null,
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Card>
            </Grid>

            {/* === FINALIZAR CADASTRO === */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 1,
                  px: 2,
                  backgroundColor: primaryColor,
                  color: "#ffffff",
                  borderTopLeftRadius: "5px",
                  borderTopRightRadius: "5px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Finalizar Cadastro
                </Typography>

                {/* Tombo do ultimo equipamento cadastrado */}
                <StyledTextField
                  label="Último Tombo Registrado"
                  size="small"
                  disabled
                  sx={{
                    width: "20%",
                    // Adicione um estilo para destacar que é apenas informativo
                    "& .MuiInputBase-input": {
                      color: "primary.main",
                      fontWeight: "bold",
                    },
                  }}
                  // Aqui usamos o dado da nova query
                  value={lastNumberData ?? "Buscando..."}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />

                {/* Checkbox Marcar Todos */}
                <Tooltip title="Marcar/Desmarcar todos os Seriais como obrigatórios">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "rgba(255,255,255,0.1)",
                      borderRadius: "4px",
                      px: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ mr: 1, fontWeight: "bold" }}
                    >
                      Marcar/Desmarcar Todos
                    </Typography>
                    <Checkbox
                      size="small"
                      indeterminate={
                        watch("equipments")?.some((eq) => eq.serial_required) &&
                        watch("equipments")?.some((eq) => !eq.serial_required)
                      }
                      checked={watch("equipments")?.every(
                        (eq) => eq.serial_required,
                      )}
                      onChange={(e) => handleToggleAllSerials(e.target.checked)}
                      sx={{
                        color: "#ffffff",
                        "&.Mui-checked": { color: "#ffffff" },
                        "&.MuiCheckbox-indeterminate": { color: "#ffffff" },
                      }}
                    />
                  </Box>
                </Tooltip>
              </Box>

              <Card
                variant="outlined"
                sx={{
                  p: 2,
                  height: "100%",
                  borderTopLeftRadius: "0px",
                  borderTopRightRadius: "0px",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {equipmentData
                    .filter(
                      (eq) =>
                        eq.transaction_item_id ===
                        selectedEquipment.raw.transaction_item_id,
                    )
                    .map((equipment, index) => (
                      <Box sx={{ display: "flex", gap: 1 }} key={equipment.id}>
                        {/* Tombo */}
                        <Controller
                          name={`equipments.${index}.number`}
                          control={control}
                          rules={{
                            validate: (value, formValues) => {
                              const hasSerial =
                                formValues.equipments[index].serial_number &&
                                formValues.equipments[
                                  index
                                ].serial_number.trim() !== "";
                              const hasNumber =
                                formValues.equipments[index].number?.trim() !==
                                "";

                              // Se o serial estiver preenchido, o tombo torna-se obrigatório
                              if (!hasNumber && hasSerial) {
                                return "Não esqueça de preencher o tombo.";
                              }
                              return true;
                            },
                          }}
                          render={({ field, fieldState }) => (
                            <StyledTextField
                              {...field}
                              value={field.value ?? ""}
                              label={`Tombo #${index + 1}`}
                              size="small"
                              fullWidth
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              slotProps={{
                                inputLabel: {
                                  shrink: true,
                                },
                              }}
                              placeholder="Digite o tombo"
                            />
                          )}
                        />

                        {/* Serial */}
                        <Controller
                          name={`equipments.${index}.serial_number`}
                          // se o serial_required for false o campo serial_number deve ser desabilitado
                          rules={{
                            validate: (value, formValues) => {
                              const isRequired =
                                formValues.equipments[index].serial_required;
                              const hasSerial =
                                formValues.equipments[index].serial_number &&
                                formValues.equipments[
                                  index
                                ].serial_number.trim() !== "";

                              // Se o checkbox de serial_required estiver marcado, o serial torna-se obrigatório
                              if (isRequired && !hasSerial && value) {
                                return "O serial é obrigatório para este item.";
                              }
                              return true;
                            },
                          }}
                          control={control}
                          render={({ field, fieldState }) => (
                            <StyledTextField
                              {...field}
                              value={field.value ?? ""}
                              label={`Serial #${index + 1}`}
                              size="small"
                              fullWidth
                              disabled={
                                !watch(`equipments.${index}.serial_required`)
                              }
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              slotProps={{
                                inputLabel: {
                                  shrink: true,
                                },
                              }}
                              placeholder="Digite o serial"
                            />
                          )}
                        />

                        {/* Checkbox de Obrigatoriedade */}
                        <Controller
                          name={`equipments.${index}.serial_required`}
                          control={control}
                          render={({ field }) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                minWidth: "120px",
                              }}
                            >
                              <Tooltip
                                title={
                                  field.value
                                    ? "Obrigatório"
                                    : "Não obrigatório"
                                }
                              >
                                {/* Container que imita o estilo do StyledTextField */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: "40px", // Altura padrão do TextField 'small'
                                    border: "1px solid",
                                    borderColor: "rgba(0, 0, 0, 0.23)", // Cor padrão da borda do MUI
                                    borderRadius: "4px",
                                    transition: "border-color 0.2s",
                                    "&:hover": {
                                      borderColor: "text.primary",
                                    },
                                  }}
                                >
                                  <Checkbox
                                    {...field}
                                    checked={field.value ?? true}
                                    onChange={(e) =>
                                      field.onChange(e.target.checked)
                                    }
                                    size="small"
                                    sx={{
                                      p: 0.5,
                                      color: primaryColor,
                                      "&.Mui-checked": { color: primaryColor },
                                    }}
                                  />
                                </Box>
                              </Tooltip>
                            </Box>
                          )}
                        />
                      </Box>
                    ))}
                </Box>
              </Card>
            </Grid>
          </Box>
        )}

        {/* BOTÃO */}
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <Button
            type="submit"
            text="Cadastrar"
            disabled={createEquipment.isPending}
          />
        </Box>
      </Box>
    </MainLayout>
  );
};

export default CreateEquipmentPage;
